import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { CATEGORY_META, FEED_SORTS, FEED_SORT_LABELS, FORM_META, LITERARY_CATEGORIES, type FeedSort, type LiteraryCategory, type LiteraryForm, type PostSummary, type PublicAuthor } from '@shared/types';
import type { SiteLayout } from '@shared/theme';
import logoMark from '@/images/logos/logo-mark.svg';
import { PostCard } from '@/components/PostCard';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { useSession } from '@/lib/session';
import { cn, initials } from '@/lib/utils';
import { categoryLabel, formLabel, locale, n, sortLabel, t } from '@/lib/i18n';

const PER_PAGE = 13; // 1 lead + 3 columns + 9 briefs

export function Home() {
  const { site, viewer } = useSession();
  const [params, setParams] = useSearchParams();
  const tenant = site?.tenant ?? null;

  const category = (params.get('category') as LiteraryCategory | null) ?? undefined;
  const form = (params.get('form') as LiteraryForm | null) ?? undefined;
  const tag = params.get('tag') ?? undefined;
  const q = params.get('q') ?? '';

  /*
   * The edition's default order. A monthly paper leads with what people are
   * reading, not with whatever was filed last — but the choice lives in the URL
   * so a reader can switch and share what they are looking at.
   */
  const sortParam = params.get('sort') as FeedSort | null;
  const sort: FeedSort = sortParam && FEED_SORTS.includes(sortParam) ? sortParam : 'popular';
  // Searching by relevance-less keyword is clearer newest-first.
  const effectiveSort: FeedSort = q ? 'recent' : sort;

  const [posts, setPosts] = useState<PostSummary[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState(q);

  useEffect(() => {
    setPage(1);
  }, [category, form, tag, q, effectiveSort]);

  const load = useCallback(
    async (signal: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.feed(
          {
            category,
            form,
            tag,
            q: q || undefined,
            // A writer's own site reads chronologically; ranking belongs to the
            // platform's edition, not to someone's personal archive.
            sort: tenant ? 'recent' : effectiveSort,
            page,
            perPage: PER_PAGE,
          },
          signal,
        );
        setPosts((current) => (page === 1 ? result.items : [...current, ...result.items]));
        setTotal(result.total);
        setHasMore(result.hasMore);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError(t('रचनाहरू ल्याउन सकिएन। पुनः प्रयास गर्नुहोस्।'));
        }
      } finally {
        setLoading(false);
      }
    },
    [category, form, tag, q, effectiveSort, tenant, page],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  /**
   * Applies several filter changes as one update.
   *
   * It takes a record rather than a single key for a reason that cost a
   * bug: two sequential calls each built `new URLSearchParams(params)` from the
   * *same* render's snapshot, so the second setParams silently discarded the
   * first. Choosing a category (set category, clear form) therefore appeared to
   * do nothing at all. One snapshot, one write.
   */
  const setFilters = (updates: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(updates)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace: true });
  };

  // Front-page hierarchy: one lead, three column stories, the rest as briefs.
  const [lead, columns, briefs] = useMemo(
    () => [posts[0], posts.slice(1, 4), posts.slice(4)] as const,
    [posts],
  );

  /* A writer's own site keeps its theme; only the platform is a broadsheet. */
  if (tenant) {
    return (
      <TenantHome
        posts={posts}
        loading={loading}
        error={error}
        total={total}
        hasMore={hasMore}
        onMore={() => setPage((current) => current + 1)}
        author={tenant}
      />
    );
  }

  return (
    <div className="broadsheet pb-16">
      <Masthead siteName={site?.siteName ?? t('कलम प्रेस')} total={total} />

      {/*
        Sections and ordering share one banded unit. They were two separately
        ruled rows, which read as two pieces of furniture stacked on top of the
        masthead's own two rules — four horizontal bands before a single word of
        writing. One band, one rule.
      */}
      <div className="border-y border-foreground/80">
        <SectionStrip
          category={category}
          tag={tag}
          onSelect={setFilters}
          search={search}
          onSearchChange={setSearch}
        />
        {/*
          Forms appear only once a category is chosen. Twenty-one of them across
          the top would be a wall of links; inside काव्य, its seven are the
          natural next question.
        */}
        {category && (
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-border/70 py-2">
            <span className="kicker np">{categoryLabel(category)}</span>
            <SectionLink active={!form} onClick={() => setFilters({ form: undefined })}>{t('सबै')}</SectionLink>
            {CATEGORY_META[category].forms.map((value) => (
              <SectionLink
                key={value}
                active={form === value}
                onClick={() => setFilters({ form: value })}
                title={FORM_META[value].hint}
              >
                {formLabel(value)}
              </SectionLink>
            ))}
          </div>
        )}

        {!q && (
          <EditionBar
            sort={sort}
            onSelect={(value) => setFilters({ sort: value === 'popular' ? undefined : value })}
          />
        )}
      </div>

      {error && (
        <div className="mt-6 border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading && page === 1 ? (
        <FrontPageSkeleton />
      ) : posts.length === 0 ? (
        <EmptyFront isWriter={Boolean(viewer)} />
      ) : (
        <>
          <div className="grid gap-8 pt-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div className="lg:border-r lg:pr-8">{lead && <PostCard post={lead} variant="lead" />}</div>

            {/* The right rail: the paper's second-tier stories. */}
            <aside className="flex flex-col">
              <h2 className="kicker rule-section pt-2">{t('थप पढ्नुहोस्')}</h2>
              <div className="mt-4 space-y-6">
                {columns.map((post) => (
                  <PostCard key={post.id} post={post} variant="column" />
                ))}
              </div>
            </aside>
          </div>

          {briefs.length > 0 && (
            <section className="mt-12">
              <h2 className="kicker rule-section pt-2">{t('अरू रचनाहरू')}</h2>
              <div className="column-rules mt-5 grid gap-x-6 md:grid-cols-2 lg:grid-cols-3">
                {[0, 1, 2].map((column) => (
                  <div key={column}>
                    {briefs
                      .filter((_, index) => index % 3 === column)
                      .map((post) => (
                        <PostCard key={post.id} post={post} variant="brief" />
                      ))}
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="mt-12 flex flex-col items-center gap-3">
            {hasMore && (
              <Button variant="outline" onClick={() => setPage((current) => current + 1)} disabled={loading}>
                {loading ? t('ल्याउँदै…') : t('थप रचना')}
              </Button>
            )}
            <p className="kicker tnum">
              {t('{shown} / {total} रचना', { shown: n(posts.length), total: n(total) })}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The masthead.
 *
 * Centred wordmark between a heavy rule and a hairline, with the dateline and
 * edition line straddling it — the arrangement every broadsheet front page uses,
 * and the thing that makes a page read as a newspaper before a word is read.
 */
function Masthead({ siteName, total }: { siteName: string; total: number }) {
  // A monthly edition, so the dateline carries the issue — month and year — not
  // today's date. Bikram Sambat would be truer to a Nepali masthead, but
  // converting it needs a lookup table no browser ships; the Gregorian month
  // rendered in Nepali is honest rather than approximated.
  const issue = new Intl.DateTimeFormat(locale(), { month: 'long', year: 'numeric' }).format(new Date());

  return (
    <header className="pt-7 text-center sm:pt-9">
      {/* The crest sits with the nameplate as one optical unit — tight to the
          name, generous above — rather than floating as a third element. */}
      <img src={logoMark} alt="" className="mx-auto h-11 w-auto sm:h-12" />

      <h1 className="masthead-title np mt-3">{siteName}</h1>

      <p className="np mt-2.5 text-sm text-muted-foreground">{t('नेपाली साहित्यको मासिक संकलन')}</p>

      <div className="rule-masthead mt-5" />

      {/* Dateline. No letterspacing and no uppercase — see .kicker in index.css. */}
      <div className="np flex flex-wrap items-center justify-center gap-x-3 gap-y-1 py-2 text-[0.78rem] text-muted-foreground sm:justify-between">
        <span>{t('अंक — {issue}', { issue: n(issue) })}</span>
        <span className="hidden sm:inline">{t('कविता · कथा · निबन्ध')}</span>
        <span className="tnum">{t('{count} प्रकाशित रचना', { count: n(total) })}</span>
      </div>
    </header>
  );
}

/**
 * How this edition is ordered.
 *
 * Set as a print "sorted by" line rather than a dropdown: on a front page the
 * available orderings are part of the furniture, and seeing all four at once is
 * what tells a reader the page is not simply reverse-chronological.
 */
function EditionBar({ sort, onSelect }: { sort: FeedSort; onSelect: (value: FeedSort) => void }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-border/70 py-2">
      <span className="kicker np">{t('यो अंक')}</span>

      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        {FEED_SORTS.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => onSelect(value)}
            title={FEED_SORT_LABELS[value].hint}
            // These are mutually exclusive, so "current item in a set" is the
            // accurate role — not a pressed toggle.
            aria-current={sort === value ? 'true' : undefined}
            className={cn(
              'np text-sm transition-colors',
              sort === value
                ? 'font-semibold text-foreground underline decoration-primary decoration-2 underline-offset-4'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {sortLabel(value)}
          </button>
        ))}
      </div>

      <span className="np ms-auto hidden text-xs text-muted-foreground sm:block">
        {FEED_SORT_LABELS[sort].hint}
      </span>
    </div>
  );
}

/** Section navigation, set as a rule-bounded strip rather than as buttons. */
function SectionStrip({
  category,
  tag,
  onSelect,
  search,
  onSearchChange,
}: {
  category?: LiteraryCategory;
  tag?: string;
  onSelect: (updates: Record<string, string | undefined>) => void;
  search: string;
  onSearchChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 py-2.5">
      <nav className="flex flex-wrap items-center gap-x-5">
        <SectionLink
          active={!category}
          onClick={() => onSelect({ category: undefined, form: undefined })}
        >{t('मुख्य पृष्ठ')}</SectionLink>
        {LITERARY_CATEGORIES.map((value) => (
          <SectionLink
            key={value}
            active={category === value}
            onClick={() => onSelect({ category: value, form: undefined })}
          >
            {categoryLabel(value)}
          </SectionLink>
        ))}
      </nav>

      {tag && (
        <span className="flex items-center gap-2 text-sm">
          <Badge className="np">{tag}</Badge>
          <button
            type="button"
            onClick={() => onSelect({ tag: undefined })}
            className="text-xs text-muted-foreground underline underline-offset-2"
          >{t('हटाउनुहोस्')}</button>
        </span>
      )}

      <form
        className="ms-auto"
        onSubmit={(event) => {
          event.preventDefault();
          onSelect({ q: search.trim() || undefined });
        }}
      >
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={t('खोज्नुहोस्…')}
            aria-label={t('रचना खोज्नुहोस्')}
            className="np h-8 w-40 rounded-none border-0 border-b bg-transparent pl-8 shadow-none focus-visible:ring-0 sm:w-52"
          />
        </div>
      </form>
    </div>
  );
}

function SectionLink({
  children,
  active,
  onClick,
  title,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        'np border-b-2 py-0.5 text-sm transition-colors',
        active
          ? 'border-primary font-semibold text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

/** Writer sites keep the quieter reading layout their theme describes. */
function TenantHome({
  posts,
  loading,
  error,
  total,
  hasMore,
  onMore,
  author,
}: {
  posts: PostSummary[];
  loading: boolean;
  error: string | null;
  total: number;
  hasMore: boolean;
  onMore: () => void;
  author: PublicAuthor;
}) {
  const { site } = useSession();
  const name = author.penName || author.displayName;
  const layout = site?.template?.layout ?? 'classic';

  /*
   * The grid layouts need the room a grid is for; the reading layouts do not.
   * A two-column magazine inside a 3xl column is two narrow columns, which is
   * worse than one comfortable one.
   */
  const wide = layout === 'magazine' || layout === 'gallery';

  return (
    <div className={cn('container py-10', wide ? 'max-w-6xl' : 'max-w-3xl')}>
      {/*
        A writer's nameplate, not the platform's.
        This is their masthead — the first thing a reader meets at their own
        address — so it is centred and given room, and the platform's newspaper
        furniture stays out of it. The name appears here and nowhere else on this
        page: the site header deliberately drops it on the front page.
      */}
      {/*
        A byline, not a monument.
        A writer's landing page is a door to the writing, so the name is set at
        the scale of a name — not a masthead. The work below is the reason
        anyone came, and a display-size nameplate over a centred column pushes it
        under the fold while saying nothing the reader did not already know from
        the address they typed.
      */}
      <header className="mb-9">
        <div className="flex items-center gap-3.5">
          <Avatar className="size-11">
            {author.avatarUrl ? <AvatarImage src={author.avatarUrl} alt="" /> : null}
            <AvatarFallback className="bg-primary text-sm text-primary-foreground">
              {initials(name)}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-0">
            <h1 className="np truncate text-2xl font-semibold leading-tight">{name}</h1>
            <div className="np mt-0.5 flex flex-wrap items-center gap-x-2.5 text-sm text-muted-foreground">
              {author.location && <span>{author.location}</span>}
              {author.location && total > 0 && <span aria-hidden="true">·</span>}
              {total > 0 && <span className="tnum">{t('{count} रचना', { count: n(total) })}</span>}
            </div>
          </div>
        </div>

        {author.bio && (
          <p className="np mt-4 max-w-prose leading-relaxed text-muted-foreground">{author.bio}</p>
        )}

        {author.websiteUrl && (
          <a
            href={author.websiteUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-block text-sm text-primary hover:underline"
          >
            {new URL(author.websiteUrl).hostname}
          </a>
        )}

        {/* A hairline in the writer's own accent — the whole of the chrome. */}
        <div className="mt-7 h-px w-12 bg-primary" />
      </header>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading && posts.length === 0 ? (
        <div className="space-y-8">
          {[0, 1, 2].map((row) => (
            <div key={row} className="space-y-3">
              <Skeleton className="h-7 w-3/4" />
              <Skeleton className="h-4 w-full" />
            </div>
          ))}
        </div>
      ) : posts.length === 0 ? (
        <p className="np py-16 text-center text-muted-foreground">{t('अहिलेसम्म कुनै रचना छैन।')}</p>
      ) : (
        <>
          <TenantPosts posts={posts} layout={layout} />
          <div className="mt-8 flex flex-col items-center gap-3">
            {hasMore && (
              <Button variant="outline" onClick={onMore} disabled={loading}>{t('थप रचना')}</Button>
            )}
            <p className="text-xs text-muted-foreground tnum">
              {n(posts.length)} / {n(total)}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * A writer's work, arranged the way their theme says.
 *
 * Each layout is a different answer to "what does a reader landing here need to
 * see first" — the excerpt, the photograph, or simply how much there is. The
 * card variants carry the differences; this only decides which one and how they
 * are placed, so a new layout is a case here and a variant there rather than a
 * second copy of this page.
 */
function TenantPosts({ posts, layout }: { posts: PostSummary[]; layout: SiteLayout }) {
  if (layout === 'magazine') {
    return (
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {posts.map((post) => (
          <PostCard key={post.id} post={post} variant="card" showAuthor={false} />
        ))}
      </div>
    );
  }

  if (layout === 'gallery') {
    return (
      <div className="grid gap-x-6 gap-y-9 sm:grid-cols-2 lg:grid-cols-3">
        {posts.map((post) => (
          <PostCard key={post.id} post={post} variant="gallery" showAuthor={false} />
        ))}
      </div>
    );
  }

  if (layout === 'index') {
    return (
      <div>
        {posts.map((post) => (
          <PostCard key={post.id} post={post} variant="index" showAuthor={false} />
        ))}
      </div>
    );
  }

  if (layout === 'minimal') {
    return (
      <div className="flex flex-col" style={{ gap: 'var(--site-gap)' }}>
        {posts.map((post) => (
          <PostCard key={post.id} post={post} variant="bare" showAuthor={false} />
        ))}
      </div>
    );
  }

  /*
   * Journal: the same rows, gathered under the year they were written in.
   *
   * The year is the unit a diary is remembered in, and it is also the only
   * grouping that costs nothing — the feed already arrives newest-first, so the
   * headings fall out of the order rather than needing a second request.
   */
  if (layout === 'journal') {
    const years: { year: string; posts: PostSummary[] }[] = [];
    for (const post of posts) {
      const year = post.publishedAt ? new Date(post.publishedAt).getFullYear().toString() : '';
      const current = years[years.length - 1];
      if (current?.year === year) current.posts.push(post);
      else years.push({ year, posts: [post] });
    }

    return (
      <div className="space-y-10">
        {years.map((group) => (
          <section key={group.year || 'undated'}>
            {group.year && (
              <h2 className="kicker tnum mb-3 border-b pb-1">
                {n(new Intl.DateTimeFormat(locale(), { year: 'numeric' }).format(new Date(`${group.year}-01-02`)))}
              </h2>
            )}
            <div className="divide-y">
              {group.posts.map((post) => (
                <PostCard key={post.id} post={post} showAuthor={false} />
              ))}
            </div>
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className="divide-y">
      {posts.map((post) => (
        <PostCard key={post.id} post={post} showAuthor={false} />
      ))}
    </div>
  );
}

function FrontPageSkeleton() {
  return (
    <div className="grid gap-8 pt-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <div className="space-y-4 lg:border-r lg:pr-8">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-4/5" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-11/12" />
      </div>
      <div className="space-y-6">
        {[0, 1, 2].map((row) => (
          <div key={row} className="space-y-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-4 w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyFront({ isWriter }: { isWriter: boolean }) {
  return (
    <div className="py-24 text-center">
      <img src={logoMark} alt="" className="mx-auto mb-5 h-16 w-auto opacity-20" />
      <p className="headline text-2xl">{t('यो अंक खाली छ।')}</p>
      <p className="np mt-1 text-sm text-muted-foreground">{t('पहिलो रचना प्रकाशित हुन बाँकी छ।')}</p>
      <Button className="mt-6" asChild>
        <Link to={isWriter ? '/dashboard/new' : '/register'}>
          {isWriter ? t('पहिलो रचना लेख्नुहोस्') : t('लेख्न सुरु गर्नुहोस्')}
        </Link>
      </Button>
    </div>
  );
}
